import * as React from 'react';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useZusatzadresseList } from 'api/zusatzadresse';
import { ZusatzadresseEdit } from 'components/Kunde/Zusatzadresse/ZusatzadresseEdit';
import { ZusatzadresseItem } from 'components/Kunde/Zusatzadresse/ZusatzadresseItem';
import { useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { Helmet } from 'react-helmet';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr 200px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

interface ZusatzadresseViewProps {
    kNr?: number;
}

interface ZusatzadresseEditData {
    kNr: number;
    typ: string;
    zaNr: number;
}

export const ZusatzadresseView: React.FC<ZusatzadresseViewProps> = ({ kNr }) => {
    const [zusatzadresseEditData, setZusatzadresseEditData] = React.useState<ZusatzadresseEditData | null>(null);
    const { urlKundeNr } = useParams<RecordViewUrlParams>();

    const [showArchiv, setShowArchiv] = React.useState(false);

    const zusatzadresseList = useZusatzadresseList({
        kNr,
        includeArchiv: showArchiv,
        withKunde: false,
        maxRows: 250,
    });

    return (
        <Fabric className={viewStyles.subItemContainer}>
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    Zusatzadressen
                </title>
            </Helmet>
            {zusatzadresseList.isLoading || zusatzadresseList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.searchBar}>
                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideForm}
                                label="Archivierte Adressen anzeigen"
                                checked={showArchiv}
                                onChange={(event, checked) => {
                                    setShowArchiv(checked || false);
                                }}
                            />
                        </div>

                        <div />
                        {kNr && (
                            <PrimaryButton
                                onClick={() =>
                                    setZusatzadresseEditData({
                                        kNr,
                                        typ: '',
                                        zaNr: -1,
                                    })
                                }
                                text="Neuer Zusatzadresse"
                            />
                        )}
                    </div>
                    <div className={viewStyles.listContainer}>
                        {zusatzadresseList?.data && zusatzadresseList?.data.length === 0 ? (
                            <div className={formStyles.noData}>Keine Zusatzadresse.</div>
                        ) : (
                            zusatzadresseList?.data?.map((zusatzadresse) => (
                                <ZusatzadresseItem
                                    key={`${zusatzadresse.typ}-${zusatzadresse.nr}`}
                                    zusatzadresse={zusatzadresse}
                                    editZusatzadresse={() =>
                                        setZusatzadresseEditData({
                                            kNr: zusatzadresse.kNr,
                                            typ: zusatzadresse.typ,
                                            zaNr: zusatzadresse.nr || 0,
                                        })
                                    }
                                />
                            ))
                        )}
                    </div>
                </>
            )}
            {zusatzadresseEditData !== null && (
                <ZusatzadresseEdit
                    kNr={zusatzadresseEditData.kNr}
                    typ={zusatzadresseEditData.typ}
                    zaNr={zusatzadresseEditData.zaNr}
                    onClose={(refetch) => {
                        setZusatzadresseEditData(null);
                        if (refetch) {
                            zusatzadresseList.refetch().then();
                        }
                    }}
                />
            )}
        </Fabric>
    );
};
